import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { toast } from 'react-hot-toast';

interface RiderFormDialogProps {
    visible: boolean;
    mode: 'create' | 'edit';
    loading?: boolean;
    onHide: () => void;
    onSubmit: (rider: Record<string, any>) => Promise<void> | void;
    rider?: any;
}

const emptyForm = {
    name: '',
    phoneNumber: '',
    email: '',
    aadharNumber: '',
    panNumber: '',
    address: '',
    pincode: '',
    status: 'pending',
};

const RiderFormDialog: React.FC<RiderFormDialogProps> = ({ visible, mode, loading = false, onHide, onSubmit, rider }) => {
    const [form, setForm] = useState({ ...emptyForm });

    useEffect(() => {
        if (!visible) {
            return;
        }

        if (mode === 'edit' && rider) {
            setForm({
                name: rider.name || '',
                phoneNumber: rider.phoneNumber || '',
                email: rider.email || '',
                aadharNumber: rider.aadharNumber || '',
                panNumber: rider.panNumber || '',
                address: rider.address || '',
                pincode: rider.pincode || '',
                status: rider.status || 'pending',
            });
            return;
        }

        setForm({ ...emptyForm });
    }, [visible, mode, rider]);

    const setField = (name: string, value: any) => {
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const buildPayload = () => {
        const payload: Record<string, any> = {
            name: form.name.trim(),
            phoneNumber: form.phoneNumber.trim(),
            email: form.email.trim(),
            aadharNumber: form.aadharNumber.trim(),
            panNumber: form.panNumber.trim(),
            address: form.address.trim(),
            pincode: form.pincode.trim(),
            status: form.status,
        };

        Object.keys(payload).forEach((key) => {
            const value = payload[key];
            if (value === '' || value === undefined || value === null) {
                delete payload[key];
            }
        });

        return payload;
    };

    const validateForm = () => {
        const requiredFields: Array<[string, string]> = [
            ['name', 'Rider name is required'],
            ['phoneNumber', 'Phone number is required'],
            ['email', 'Email is required'],
            ['address', 'Address is required'],
            ['pincode', 'Pincode is required'],
            ['status', 'Status is required'],
        ];

        for (const [field, message] of requiredFields) {
            if (!String(form[field as keyof typeof form] ?? '').trim()) {
                return message;
            }
        }

        return '';
    };

    const handleSubmit = async () => {
        const validationError = validateForm();
        if (validationError) {
            toast.error(validationError);
            return;
        }

        await onSubmit(buildPayload());
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label="Cancel" severity="secondary" outlined onClick={onHide} type="button" />
            <Button label={mode === 'create' ? 'Create Rider' : 'Save Changes'} icon="pi pi-check" onClick={handleSubmit} loading={loading} type="button" />
        </div>
    );

    return (
        <Dialog
            header={mode === 'create' ? 'Create Rider' : 'Edit Rider'}
            visible={visible}
            onHide={onHide}
            modal
            maximizable
            style={{ width: '68vw', minWidth: '860px' }}
            footer={footer}
            className="p-fluid"
        >
            <div className="grid">
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Name</label>
                    <InputText required value={form.name} onChange={(e) => setField('name', e.target.value)} placeholder="Rider name" />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Phone Number</label>
                    <InputText required value={form.phoneNumber} onChange={(e) => setField('phoneNumber', e.target.value)} placeholder="9999999999" />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Email</label>
                    <InputText required value={form.email} onChange={(e) => setField('email', e.target.value)} placeholder="rider@example.com" />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Status</label>
                    <Dropdown
                        required
                        value={form.status}
                        options={[
                            { label: 'Pending', value: 'pending' },
                            { label: 'Verified', value: 'verified' },
                            { label: 'Blocked', value: 'blocked' },
                        ]}
                        onChange={(e) => setField('status', e.value)}
                        placeholder="Select status"
                    />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Aadhar Number</label>
                    <InputText value={form.aadharNumber} onChange={(e) => setField('aadharNumber', e.target.value)} placeholder="456213884548" />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">PAN Number</label>
                    <InputText value={form.panNumber} onChange={(e) => setField('panNumber', e.target.value)} placeholder="ABCDG1234G" />
                </div>
                <div className="col-12">
                    <label className="block mb-2 font-medium">Address</label>
                    <InputTextarea required rows={3} value={form.address} onChange={(e) => setField('address', e.target.value)} placeholder="Malakpet" />
                </div>
                <div className="col-12 md:col-6">
                    <label className="block mb-2 font-medium">Pincode</label>
                    <InputText required value={form.pincode} onChange={(e) => setField('pincode', e.target.value)} placeholder="500036" />
                </div>
            </div>
        </Dialog>
    );
};

export default RiderFormDialog;
